import {
  type CheckInOutType,
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useFinalyticConnectionQuery,
  useInfiniteQuery,
  useOwnerPortalContact,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { day, ensure, hasValue, isDate } from '@vrplatform/utils';
import { Box, Center, Group, Text, Tooltip } from '@mantine/core';
import type { paidStatus_enum } from '@vrplatform/graphql';
import {
  getListingName,
  orderByListing,
  whereConnectionStatusDefault,
  whereListings,
} from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import { getListingDeactivation } from '../../listings/detail/useListingDetailViewQuery';
import { useReservationsTableFilter } from './useReservationsTableFilter';
import { CustomFilter } from '../../../components';

export const ReservationsTableFilter = ({
  hide,
  showMonthSelect,
}: {
  hide?: (
    | 'connection'
    | 'booking-channel'
    | 'booking-status'
    | 'date'
    | 'status'
    | 'listing'
    | 'business-model'
    | 'financial'
  )[];
  showMonthSelect?: boolean;
}) => {
  'use memo';

  const { filter, setFilter } = useReservationsTableFilter();
  const [dashboard] = useDashboard();

  return (
    <Group gap="xs">
      <Filter.Search
        value={filter.search || ''}
        setValue={(v) => setFilter({ search: v || null })}
      />
      {!hide?.includes('status') && dashboard !== 'owner' && (
        <GeneralLedgerStatus />
      )}
      {!hide?.includes('date') && <Date />}
      {showMonthSelect && <Month />}
      {!hide?.includes('booking-status') && <BookingStatus />}
      {dashboard !== 'owner' && <PaidStatus />}
      {!hide?.includes('connection') && dashboard !== 'owner' && <Connection />}
      {!hide?.includes('booking-channel') && <BookingChannel />}
      {!hide?.includes('listing') && <Listing />}
      {!hide?.includes('business-model') && dashboard !== 'owner' && (
        <BusinessModel />
      )}
      {!hide?.includes('financial') && dashboard !== 'owner' && <Financial />}
    </Group>
  );
};

const Month = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  type Month = Date | null | undefined;

  const date = useMemo<Month>(() => {
    const defaultDate = day().startOf('month').toDate();

    if (!filter.date) return defaultDate;

    const [v] = filter.date.split('...');

    if (!isDate(v)) return defaultDate;

    return day(v).startOf('month').toDate();
  }, [filter.date]);

  return (
    <Filter.Month
      value={date}
      setValue={(value) => {
        value &&
          setFilter({
            date: [
              day(value).startOf('month').yyyymmdd(),
              day(value).endOf('month').yyyymmdd(),
            ].join('...'),
          });
      }}
      type="default"
      label="Month"
    />
  );
};

const Date = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  return (
    <Filter.Date
      value={filter.date || undefined}
      setValue={(value) => {
        setFilter({
          date:
            value
              ?.filter(hasValue)
              .map((date) => day(date).yyyymmdd())
              .join('...') || null,
        });
      }}
      dateType={(filter.dateType as CheckInOutType) || 'all'}
      setDateType={(v) => setFilter({ dateType: v })}
    />
  );
};

const GeneralLedgerStatus = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  return (
    <Filter.Status
      value={filter.generalLedgerStatus ?? 'active'}
      setValue={(v) => v && setFilter({ generalLedgerStatus: v })}
      inactiveLabel="Archived"
    />
  );
};

const BookingStatus = () => {
  const { filter, setFilter } = useReservationsTableFilter();
  const [dashboard] = useDashboard();

  const options: SelectItem[] = useMemo<SelectItem[]>(() => {
    return ensure<(SelectItem | undefined)[]>([
      {
        label: 'Booked',
        value: 'booked',
        icon: (
          <Center
            sx={(theme) => ({
              width: 8,
              height: 8,
              backgroundColor: theme.colors.green[6],
              borderRadius: '50%',
              alignSelf: 'center',
            })}
          />
        ),
      },
      {
        label: 'Canceled',
        value: 'canceled',
        icon: (
          <Center
            sx={(theme) => ({
              width: 8,
              height: 8,
              backgroundColor: theme.colors.orange[6],
              borderRadius: '50%',
              alignSelf: 'center',
            })}
          />
        ),
      },
      dashboard === 'propertyManager'
        ? {
            label: 'Deleted',
            value: 'inactive',
            icon: (
              <Center
                sx={(theme) => ({
                  width: 8,
                  height: 8,
                  backgroundColor: theme.colors.red[6],
                  borderRadius: '50%',
                  alignSelf: 'center',
                })}
              />
            ),
          }
        : undefined,
    ]).filter(hasValue);
  }, [dashboard]);

  const value: SelectItem | null =
    options.find((o) => o.value === filter.status) || null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ status: v?.value || null })}
      label="Booking Status"
      type="single"
      hideIcon
      data={{
        options,
      }}
    />
  );
};

const PaidStatus = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  const options: SelectItem<NonNullable<paidStatus_enum>>[] = [
    {
      label: 'Not (fully) paid',
      value: 'unpaid',
      icon: (
        <Center
          sx={(theme) => ({
            width: 8,
            height: 8,
            backgroundColor: theme.colors.yellow[6],
            borderRadius: '50%',
            alignSelf: 'center',
          })}
        />
      ),
    },
    // {
    //   label: 'Not fully',
    //   value: 'underpaid',
    //   icon: (
    //     <Center
    //       sx={(theme) => ({
    //         width: 8,
    //         height: 8,
    //         backgroundColor: theme.colors.yellow[6],
    //         borderRadius: '50%',
    //         alignSelf: 'center',
    //       })}
    //     />
    //   ),
    // },
    {
      label: 'Paid fully',
      value: 'paid',
      icon: (
        <Center
          sx={(theme) => ({
            width: 8,
            height: 8,
            backgroundColor: theme.colors.green[6],
            borderRadius: '50%',
            alignSelf: 'center',
          })}
        />
      ),
    },
    // {
    //   label: 'Overpaid',
    //   value: 'overpaid',
    //   icon: (
    //     <Center
    //       sx={(theme) => ({
    //         width: 8,
    //         height: 8,
    //         backgroundColor: theme.colors.orange[6],
    //         borderRadius: '50%',
    //         alignSelf: 'center',
    //       })}
    //     />
    //   ),
    // },
  ];

  const value: SelectItem | null =
    options.find((o) => o.value === filter.paidStatus) || null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ paidStatus: v?.value || null })}
      label="Payment Status"
      type="single"
      hideIcon
      data={{
        options,
      }}
    />
  );
};

const Connection = () => {
  const [{ id: teamId }] = useTeam();
  const { finalyticConnectionId, loading: loadingFinalyticConnection } =
    useFinalyticConnectionQuery();
  const { filter, setFilter } = useReservationsTableFilter();
  const [search, setSearch] = useState('');

  const connectionIds = filter.connectionIds || [];
  const gate = useSelectQueryGate({ value: connectionIds });

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.connection_bool_exp = {
        tenantId: { _eq: teamId },
        reservations: {},
        app: {
          id: { _neq: 'finalytic' },
        },
        status: whereConnectionStatusDefault,
        _or: search
          ? [
              {
                name: { _ilike: `%${search}%` },
              },
            ]
          : undefined,
      };

      const list = q
        .connection({
          where,
          order_by: [{ name: 'asc' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.name || 'Missing connection name',
        }));

      const aggregate =
        q.connectionAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['reservations', 'connections'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data } = useQuery(
    (q, { connectionIds, finalyticConnectionId }) => {
      if (!finalyticConnectionId) {
        return {
          value: [] as SelectItem[],
        };
      }

      if (
        connectionIds.includes(finalyticConnectionId) &&
        connectionIds.length === 1
      ) {
        return {
          value: [
            {
              label: 'Manual reservations',
              value: finalyticConnectionId,
            },
          ],
        };
      }

      const value = q
        .connection({ where: { id: { _in: connectionIds } } })
        .map<SelectItem>((item) => ({
          label: item.name || '',
          value: item.id,
        }));

      return {
        value,
      };
    },
    {
      skip:
        !teamId ||
        loadingFinalyticConnection ||
        !finalyticConnectionId ||
        !gate.shouldFetchValue,
      queryKey: ['reservations', 'connections'],
      keepPreviousData: true,
      variables: {
        connectionIds,
        finalyticConnectionId,
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  return (
    <Filter.Select
      value={value}
      setValue={(v) =>
        setFilter({
          connectionIds: v
            .map((i) => i.value)
            .filter((x) => x !== finalyticConnectionId),
        })
      }
      label="Connection"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      customBottomActions={
        finalyticConnectionId
          ? [
              {
                id: 'manual',
                label: 'Manually added reservations',
                onSubmit: () =>
                  setFilter({
                    connectionIds: connectionIds.includes(finalyticConnectionId)
                      ? connectionIds.filter(
                          (id) => id !== finalyticConnectionId
                        )
                      : [finalyticConnectionId],
                  }),
              },
            ]
          : []
      }
    />
  );
};

const Listing = () => {
  const [dashboard] = useDashboard();
  const { filter, setFilter } = useReservationsTableFilter();
  const [{ id: teamId }] = useTeam();

  const { contactId: ownerPortalContactId } = useOwnerPortalContact();

  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: filter.listingIds });
  const shouldPrefetchOptions = dashboard === 'owner' && !search.trim();
  const shouldFetchOptions = gate.shouldFetchOptions || shouldPrefetchOptions;

  const queryData = useInfiniteQuery(
    (
      q,
      { teamId, search, dashboard, ownerPortalContactId, today },
      { limit, offset }
    ) => {
      const where: gqlV2.listing_bool_exp = whereListings({
        tenantId: teamId,
        dashboard,
        search,
        ownerPortalContactId,
        GL: true,
        status: 'all',
      });

      const list = q
        .listings({
          where,
          order_by: [orderByListing],
          limit,
          offset,
        })
        .map<SelectItem>((res) => {
          const label = getListingName(res);
          const status = getListingDeactivation(res, today).status.includes(
            'disabled'
          )
            ? 'disabled'
            : 'active';

          return {
            value: res.id,
            label:
              status === 'disabled'
                ? ((
                    <Group w="100%" justify="space-between" wrap="nowrap">
                      <Text>{label}</Text>
                      <Tooltip label="This listing is disabled or has not been assigned an ownership yet">
                        <Box
                          sx={(theme) => ({
                            width: 8,
                            height: 8,
                            backgroundColor: theme.colors.yellow[6],
                            borderRadius: '50%',
                          })}
                        />
                      </Tooltip>
                    </Group>
                  ) as any)
                : label,
          };
        });

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !shouldFetchOptions,
      queryKey: ['reservations', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
        dashboard,
        ownerPortalContactId,
        today: day().yyyymmdd(),
      },
    }
  );

  const { data } = useQuery(
    (q, { listingIds }) => {
      const value = q
        .listings({ where: { id: { _in: listingIds } } })
        .map<SelectItem>((item) => ({
          label: getListingName(item),
          value: item.id,
        }));

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['reservations', 'listings'],
      keepPreviousData: true,
      variables: {
        listingIds: (filter.listingIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  if (dashboard === 'owner' && shouldPrefetchOptions) {
    if (queryData.isLoading || !queryData.data) {
      return <LoadingIndicator size="xs" />;
    }

    if ((queryData.data?.pages?.[0]?.aggregate ?? 0) < 2) return null;
  }

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ listingIds: v.map((i) => i.value) })}
      label="Listing"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

type BusinessModelEnum = NonNullable<
  ReturnType<typeof useReservationsTableFilter>['filter']['businessModel']
>;

const BUSINESS_MODELS: Record<
  BusinessModelEnum,
  SelectItem<BusinessModelEnum>
> = {
  managed: {
    label: 'Managed',
    value: 'managed',
  },
  co_host: {
    label: 'Co-Host',
    value: 'co_host',
  },
  co_host_airbnb: {
    label: 'Co-Host (Airbnb only)',
    value: 'co_host_airbnb',
  },
};

const BusinessModel = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  const value =
    Object.values(BUSINESS_MODELS).find(
      (v) => v.value === filter.businessModel
    ) || null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ businessModel: v?.value || null })}
      label="Business Model"
      type="single"
      withinPortal
      data={{
        options: Object.values(BUSINESS_MODELS),
        loading: false,
        error: null,
        sort: null,
      }}
    />
  );
};

const BookingChannel = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  return (
    <CustomFilter.BookingChannel
      bookingChannels={filter.bookingChannels}
      setBookingChannels={(v) =>
        setFilter({ bookingChannels: v?.length ? v : null })
      }
    />
  );
};

const Financial = () => {
  const { filter, setFilter } = useReservationsTableFilter();

  return (
    <CustomFilter.ReservationLineType
      lineType={filter.lineType}
      setLineType={(v) => setFilter({ lineType: v })}
    />
  );
};
